/**
 * DependencyCard Component
 * Displays a single infrastructure dependency status
 */

import React from 'react';
import { Popup } from 'semantic-ui-react';
import { I18n } from 'react-redux-i18n';
import { 
  Database01, 
  Server01, 
  HardDrive, 
  Dataflow02 
} from '@untitledui/icons';
import { DependencyStatus, DependencyKey, HealthStatus, LatencyHistoryEntry } from '../types';
import { calculateDependencyStatus } from '../helpers/statusCalculators';
import StatusDot from './StatusDot';
import LatencyBar from './LatencyBar';
import LatencySparkline from './LatencySparkline';
import styles from '../SystemHealth.module.css';

const I18N_PREFIX = 'systemSettings.systemHealth';

interface DependencyCardProps {
  dependencyKey: DependencyKey;
  label: string;
  dependency: DependencyStatus;
  history: LatencyHistoryEntry[];
  isRefreshing?: boolean;
}

const iconMap: Record<DependencyKey, React.FC<{ size?: number; className?: string }>> = {
  database: Database01,
  cache: Server01,
  blobStorage: HardDrive,
  serviceBus: Dataflow02
};

export const DependencyCard: React.FC<DependencyCardProps> = ({
  dependencyKey,
  label,
  dependency,
  history,
  isRefreshing = false
}) => {
  const status = calculateDependencyStatus(dependencyKey, dependency);
  const IconComponent = iconMap[dependencyKey];
  const hasError = dependency.error !== null;

  const cardContent = (
    <div 
      className={`${styles.dependencyCard} ${isRefreshing ? styles.refreshing : ''}`}
      data-status={status}
    >
      {/* Header */}
      <div className={styles.dependencyHeader}>
        <IconComponent size={20} className={styles.dependencyIcon} />
        <span className={styles.dependencyLabel}>{I18n.t(label)}</span>
      </div>

      {/* Status */}
      <div className={styles.dependencyStatus}>
        <StatusDot status={status} size="small" />
        <span className={`${styles.statusText} ${styles[`status${status.charAt(0).toUpperCase() + status.slice(1)}`]}`}>
          {I18n.t(`${I18N_PREFIX}.status.${status}`)}
        </span>
      </div>

      {/* Latency */}
      <div className={styles.dependencyLatency}>
        <span className={styles.latencyValue}>{dependency.latencyMs}</span>
        <span className={styles.latencyUnit}>{I18n.t(`${I18N_PREFIX}.dependencies.latencyUnit`)}</span>
        <LatencySparkline 
          history={history} 
          dependencyKey={dependencyKey} 
          status={status}
        />
      </div>

      {/* Latency bar */}
      <LatencyBar 
        dependencyKey={dependencyKey} 
        latencyMs={dependency.latencyMs} 
        status={status}
      />

      {/* Connection status */}
      <div className={styles.connectionStatus}>
        {dependency.status === 'connected' 
          ? I18n.t(`${I18N_PREFIX}.dependencies.connected`) 
          : I18n.t(`${I18N_PREFIX}.dependencies.disconnected`)}
      </div>

      {/* Error indicator (visual only, details in popup) */}
      {hasError && (
        <div className={styles.errorIndicator}>
          {I18n.t(`${I18N_PREFIX}.dependencies.errorDetails`)}
        </div>
      )}
    </div>
  );

  // Wrap with Popup if there's an error
  if (hasError && dependency.error) {
    return (
      <Popup
        trigger={cardContent}
        content={dependency.error}
        position="top center"
        wide="very"
        className={styles.errorPopup}
      />
    );
  }

  return cardContent;
};

export default DependencyCard;
